import React, { useState, useRef, useEffect } from 'react'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { ReactComponent as MicrophoneIcon } from 'assets/svgs/microphone-icon.svg'
import { ReactComponent as TrashIcon } from 'assets/svgs/trash.svg'

interface AppAudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob, transcript?: string, duration?: number) => void
  onRecordingStart?: () => void
  onRecordingStop?: () => void
  disabled?: boolean
  maxDuration?: number // in seconds
  className?: string
}

export const AppAudioRecorder: React.FC<AppAudioRecorderProps> = ({
  onRecordingComplete,
  onRecordingStart,
  onRecordingStop,
  disabled = false,
  maxDuration = 60, // 1 minute default
  className = '',
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [silenceCountdown, setSilenceCountdown] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recognitionRef = useRef<any>(null)
  const transcriptRef = useRef<string>('')
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastTranscriptRef = useRef<string>('')

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (silenceTimerRef.current) {
        clearInterval(silenceTimerRef.current)
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      })

      streamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      })

      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      transcriptRef.current = ''
      lastTranscriptRef.current = ''

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' })

        // Stop speech recognition
        if (recognitionRef.current) {
          recognitionRef.current.stop()
          recognitionRef.current = null
        }

        // Clear silence timer
        if (silenceTimerRef.current) {
          clearInterval(silenceTimerRef.current)
          silenceTimerRef.current = null
        }

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }

        // Ensure duration is at least 1 second if there was any recording
        const finalDuration = recordingTime > 0 ? recordingTime : audioBlob.size > 0 ? 1 : 0
        onRecordingComplete(audioBlob, transcriptRef.current, finalDuration)
      }

      // Start speech recognition for live transcription
      startSpeechRecognition()

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
      onRecordingStart?.()

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1
          if (newTime >= maxDuration) {
            stopRecording()
          }
          return newTime
        })
      }, 1000)

      // Start initial silence timer (in case user doesn't speak at all)
      let initialCountdown = 5
      setSilenceCountdown(initialCountdown)

      const initialInterval = setInterval(() => {
        initialCountdown -= 1
        setSilenceCountdown(initialCountdown)

        if (initialCountdown <= 0) {
          clearInterval(initialInterval)
          stopRecording()
        }
      }, 1000)

      silenceTimerRef.current = initialInterval as any
    } catch (error) {
      console.error('Error starting recording:', error)
      alert('Unable to access microphone. Please check your permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      onRecordingStop?.()

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      if (silenceTimerRef.current) {
        clearInterval(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      setSilenceCountdown(0)
    }
  }

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Stop recording without triggering onRecordingComplete
      mediaRecorderRef.current.stop()
      setIsRecording(false)

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      if (silenceTimerRef.current) {
        clearInterval(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      setSilenceCountdown(0)

      // Stop speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionRef.current = null
      }

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }

      // Reset refs
      transcriptRef.current = ''
      lastTranscriptRef.current = ''
    }
  }

  const startSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported')
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setIsTranscribing(true)
    }

    recognition.onresult = (event) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' '
        } else {
          interimTranscript += transcript
        }
      }

      // Reset silence timer when ANY speech is detected (final or interim)
      if (finalTranscript || interimTranscript) {
        if (silenceTimerRef.current) {
          clearInterval(silenceTimerRef.current)
          silenceTimerRef.current = null
        }

        setSilenceCountdown(0)

        // Start 3-second silence timer with countdown
        let countdown = 3
        setSilenceCountdown(countdown)

        const countdownInterval = setInterval(() => {
          countdown -= 1
          setSilenceCountdown(countdown)

          if (countdown <= 0) {
            clearInterval(countdownInterval)
            stopRecording()
          }
        }, 1000)

        silenceTimerRef.current = countdownInterval as any
      }

      // Update the final transcript reference only for final results
      if (finalTranscript && finalTranscript.trim() !== lastTranscriptRef.current) {
        transcriptRef.current += finalTranscript
        lastTranscriptRef.current = finalTranscript.trim()
      }
    }

    recognition.onend = () => {
      setIsTranscribing(false)

      // Restart recognition if we're still recording (recognition can end unexpectedly)
      if (isRecording && recognitionRef.current) {
        setTimeout(() => {
          if (isRecording && recognitionRef.current) {
            try {
              recognitionRef.current.start()
              setIsTranscribing(true)
            } catch (error) {
              console.error('Error restarting speech recognition:', error)
            }
          }
        }, 100)
      }
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsTranscribing(false)
    }

    recognitionRef.current = recognition
    recognition.start()
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={`${className}`}>
      {!isRecording ? (
        <AppButton
          variant="clear"
          size="sm"
          disabled={disabled || isTranscribing}
          onClick={startRecording}
          label={isTranscribing ? 'Transcribing...' : 'Voice Input'}
          LeftIcon={() => <MicrophoneIcon className="w-5 h-5 text-app-grey-60" />}
        />
      ) : (
        <div className="flex items-center gap-2 p-2 bg-white border border-dashed border-app-primary-40 rounded-lg min-w-[300px]">
          {/* Cancel button */}
          <button
            onClick={cancelRecording}
            className="p-1 hover:bg-app-grey-10 rounded transition-colors flex-shrink-0"
            title="Cancel recording"
          >
            <TrashIcon className="w-4 h-4 text-app-grey-60" />
          </button>

          {/* Recording indicator */}
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>

          {/* Waveform visualization */}
          <div className="flex items-center gap-[1px] flex-1 px-2">
            {[...Array(40)].map((_, i) => (
              <div
                key={i}
                className="w-[2px] bg-app-primary-60 rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 16 + 4}px`,
                  animationDelay: `${i * 50}ms`,
                }}
              />
            ))}
          </div>

          {/* Time display */}
          <div className="flex flex-col items-end flex-shrink-0 min-w-[40px]">
            <span className="text-sm text-app-grey-70 font-mono">{formatTime(recordingTime)}</span>
            {silenceCountdown > 0 && (
              <span className="text-xs text-orange-600 font-mono">{silenceCountdown}s</span>
            )}
          </div>

          {/* Stop button */}
          <button
            onClick={stopRecording}
            className="p-1 bg-app-primary-60 hover:bg-app-primary-70 text-white rounded transition-colors flex-shrink-0"
            title="Stop recording"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="6" y="6" width="12" height="12" fill="currentColor" rx="2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    SpeechRecognition: any
    webkitSpeechRecognition: any
  }
}
